import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { sandboxApi, tutorsApi } from '../../services/api';
import Lottie from 'react-lottie';
import catAnimation from '../../img/cat.json';
import loadingAnimation from '../../img/generator_loader.json';
import WelcomeTooltip from './WelcomeTooltip';
import { Copy, Send, Loader } from 'lucide-react';

const AIAssistant = ({ 
    token, 
    sandboxId, 
    userId, 
    language, 
    selectedComponent, 
    selectedTab, 
    initialPrompt,
    aiTutor = null
}) => {
    const { t } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);
    const [conversation, setConversation] = useState([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showWelcomeTooltip, setShowWelcomeTooltip] = useState(!aiTutor);
    const [conversationId, setConversationId] = useState(null);
    // State for AI tutor - can be passed as prop or fetched from API
    const [currentAITutor, setCurrentAITutor] = useState(aiTutor);
    
    // Update showWelcomeTooltip when currentAITutor changes
    useEffect(() => {
        setShowWelcomeTooltip(!currentAITutor);
    }, [currentAITutor]);
    // State variables for system configuration
    const [components, setComponents] = useState([]);
    const [relations, setRelations] = useState([]);
    const [instance, setInstance] = useState(null);
    const chatRef = useRef(null);
    const assistantRef = useRef(null);
    const textareaRef = useRef(null);

    // Create conversation for AI-tutor or standard assistant when mounting
    useEffect(() => {
        const initConversation = async () => {
            // Try to get conversation ID from localStorage first
            const storedConversationId = localStorage.getItem(`sandbox_conversation_${sandboxId}`);
            
            if (storedConversationId) {
                // Set the conversation ID from localStorage
                setConversationId(storedConversationId);
                
                // Also try to load previous messages
                const storedMessages = localStorage.getItem(`sandbox_messages_${sandboxId}`);
                if (storedMessages) {
                    try {
                        const parsedMessages = JSON.parse(storedMessages);
                        setConversation(parsedMessages);
                    } catch (error) {
                        console.error('Error parsing stored messages:', error);
                    }
                }

            } else if (!conversationId && currentAITutor) {
                // No stored conversation ID and AI tutor exists, create a new one
                try {
                    // Create conversation for AI-tutor
                    const response = await tutorsApi.createConversation(
                        t('ai_assistant.conversation_title', {sandboxId: sandboxId}),
                        currentAITutor.id,
                        token
                    );
                    setConversationId(response.id);
                    // Store the new conversation ID in localStorage
                    localStorage.setItem(`sandbox_conversation_${sandboxId}`, response.id);
                } catch (error) {
                    console.error('Error creating conversation:', error);
                }
            }
        };
        
        initConversation();
    }, [currentAITutor, token, sandboxId, conversationId, t]);

    // Fetch system configuration data
    // This data is fetched for reference on the frontend, but the backend already
    // retrieves this information from the database when needed.
    // The AI tutor needs this information to provide context-aware assistance.
    useEffect(() => {
        const fetchSystemData = async () => {
            if (token && sandboxId) {
                try {
                    // Fetch components
                    const componentsData = await sandboxApi.getComponents(sandboxId, token);
                    setComponents(componentsData);

                    // Fetch relations
                    if (componentsData && componentsData.length > 0) {
                        const componentIds = componentsData.map(comp => comp.id);
                        const relationsData = await sandboxApi.getComponentsRelations(sandboxId, componentIds, token);
                        setRelations(relationsData);
                    }

                    // Fetch instance data
                    const instancesData = await sandboxApi.getInstances(sandboxId, token);
                    if (instancesData && instancesData.length > 0) {
                        setInstance(instancesData[0]);
                    }
                } catch (error) {
                    console.error('Error fetching system configuration data:', error);
                }
            }
        };
        
        fetchSystemData();
    }, [token, sandboxId]);
    
    // Auto resize textarea
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
        }
    }, [input]);

    const handleAssistantResponse = useCallback(async (userInput) => {
        setIsLoading(true);
        setConversation(prev => [...prev, { 
            type: 'user', 
            content: userInput,
            timestamp: new Date().toISOString()
        }]);
        
        try {
            const requestData = {
                user_input: userInput,
                sandbox_id: sandboxId,
                language: language,
                interface_state: {
                    selected_component: selectedComponent,
                    selected_tab: selectedTab
                },
                user_id: userId,
                // Include system configuration data for frontend reference only
                // The backend already fetches this data from the database
                _system_config_frontend_only: {
                    components: components.map(comp => ({
                        id: comp.id,
                        name: comp.name,
                        type: comp.type?.name,
                        status: comp.status,
                        description: comp.description,
                        configuration: comp.configuration
                    })),
                    relations: relations.map(rel => ({
                        from: rel.from_component?.name,
                        to: rel.to_component?.name,
                        type: rel.relation_type,
                        description: rel.description
                    })),
                    architecture: instance?.architecture || '',
                    prompt: instance?.prompt || '',
                    instructions: t('ai_assistant.instructions')
                }
            };

            // Always add conversation_id, even for standard assistant
            if (conversationId) {
                requestData.conversation_id = conversationId;
            }

            const response = await sandboxApi.getAssistantResponse(requestData, token);

            // If the backend returns a different conversation_id, update our state and localStorage
            if (response.conversation_id && response.conversation_id !== conversationId) {
                setConversationId(response.conversation_id);
                localStorage.setItem(`sandbox_conversation_${sandboxId}`, response.conversation_id);
            }

            if (!currentAITutor && response.ai_tutor) {
                try {
                    const tutorDetailsResponse = await tutorsApi.getTutor(response.ai_tutor, token);
                    setCurrentAITutor(tutorDetailsResponse);
                } catch (error) {
                    console.error(`Error fetching AI tutor details: ${error}`);
                }
            }
            
            setConversation(prev => [...prev, { 
                type: 'assistant', 
                content: response.response,
                timestamp: new Date().toISOString()
            }]);
            setIsExpanded(true);
        } catch (error) {
            console.error('Error getting assistant response:', error);
            setConversation(prev => [...prev, { 
                type: 'assistant', 
                content: t('ai_assistant.error_message'),
                timestamp: new Date().toISOString()
            }]);
            setIsExpanded(true);
        } finally {
            setIsLoading(false);
        }
    }, [token, sandboxId, language, selectedComponent, selectedTab, userId, t, conversationId, components, relations, instance, currentAITutor]);

    const handleAcceptHelp = () => {
        setIsExpanded(true);
        const welcomeMessage = currentAITutor 
            ? t('ai_assistant.tutor_welcome_message', {name: currentAITutor.name})
            : t('ai_assistant.initial_prompt');
        handleAssistantResponse(welcomeMessage);
    };

    useEffect(() => {
        // Only send the initial prompt if we have a conversation ID
        if (initialPrompt && conversationId) {
            handleAssistantResponse(initialPrompt);
        }
    }, [initialPrompt, handleAssistantResponse, conversationId]);

    // Save conversation to localStorage when it changes
    useEffect(() => {
        if (conversation.length > 0 && sandboxId) {
            localStorage.setItem(`sandbox_messages_${sandboxId}`, JSON.stringify(conversation));
        }
    }, [conversation, sandboxId]);

    useEffect(() => {
        if (chatRef.current) {
            chatRef.current.scrollTop = chatRef.current.scrollHeight;
        }
    }, [conversation, isLoading]);

    const handleSubmit = useCallback((e) => {
        e.preventDefault();
        if (input.trim()) {
            handleAssistantResponse(input);
            setInput('');
            if (textareaRef.current) {
                textareaRef.current.style.height = 'auto';
            }
        }
    }, [input, handleAssistantResponse]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        }
    };

    const catLottieOptions = {
        loop: true,
        autoplay: true,
        animationData: catAnimation,
        rendererSettings: {
            preserveAspectRatio: 'xMidYMid slice'
        }
    };

    const loadingLottieOptions = {
        loop: true,
        autoplay: true,
        animationData: loadingAnimation,
        rendererSettings: {
            preserveAspectRatio: 'xMidYMid slice'
        }
    };

    const handleWheel = useCallback((e) => {
        e.stopPropagation();
    }, []);

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
    };

    return (
        <div className="fixed bottom-4 right-4 z-50" ref={assistantRef}>
            {showWelcomeTooltip && !currentAITutor && (
                <WelcomeTooltip
                    onAccept={handleAcceptHelp}
                    onDecline={() => setShowWelcomeTooltip(false)}
                />
            )}
            {!isExpanded && !isLoading ? (
                <div 
                    onClick={() => setIsExpanded(true)}
                    className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center shadow-lg hover:bg-orange-600 transition-colors cursor-pointer"
                    title={t('ai_assistant.open_chat')}
                >
                    <Lottie options={catLottieOptions} height={50} width={50} />
                </div>
            ) : !isExpanded && isLoading ? (
                <div className="flex flex-col items-center">
                    <Lottie options={loadingLottieOptions} height={100} width={100} />
                    <span className="text-sm mt-2 text-orange-500">{t('ai_assistant.loading')}</span>
                </div>
            ) : (
                <div
                    className="bg-gray-900 rounded-lg shadow-xl flex flex-col"
                    style={{width: '600px', height: '80vh', maxHeight: '800px'}}
                    onWheel={handleWheel}
                >
                    <div className="p-3 bg-gray-800 text-white rounded-t-lg flex justify-between items-center border-b border-gray-700">
                        <h3 className="text-sm font-semibold flex items-center">
                            <div className="mr-2" style={{ width: '20px', height: '20px' }}>
                                <Lottie options={catLottieOptions} height={20} width={20} />
                            </div>
                            {currentAITutor ? currentAITutor.name : t('ai_assistant.title')}
                        </h3>
                        <button 
                            onClick={() => setIsExpanded(false)} 
                            className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-gray-700"
                        >
                            &times;
                        </button>
                    </div>
                    <div
                        ref={chatRef}
                        className="flex-grow overflow-y-auto p-4 space-y-4 text-sm"
                        style={{
                            height: 'calc(100% - 120px)',
                            overflowX: 'hidden',
                            backgroundColor: '#111827' // gray-900
                        }}
                    >
                        {conversation.length === 0 ? (
                            <div className="h-full flex items-center justify-center">
                                <div className="text-center p-6 max-w-md">
                                    <div className="mx-auto mb-4" style={{ width: '48px', height: '48px' }}>
                                        <Lottie options={catLottieOptions} height={48} width={48} />
                                    </div>
                                    <h3 className="text-xl font-bold text-white mb-2">
                                        {t('ai_assistant.start_conversation')}
                                    </h3>
                                    <p className="text-gray-400 mb-4">
                                        {t('ai_assistant.ask_question')}
                                    </p>
                                </div>
                            </div>
                        ) : (
                            conversation.map((message, index) => (
                                <div key={index} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                                    <div
                                        className={`max-w-[85%] rounded-lg p-3 ${
                                            message.type === 'user'
                                                ? 'bg-orange-500 text-white'
                                                : 'bg-gray-800 border border-gray-700 text-white'
                                        }`}
                                    >
                                        {message.type === 'assistant' ? (
                                            <ReactMarkdown
                                                className="prose prose-invert max-w-none text-sm"
                                                components={{
                                                    code({node, inline, className, children, ...props}) {
                                                        const match = /language-(\w+)/.exec(className || '');
                                                        return !inline && match ? (
                                                            <div className="relative mt-3 mb-3">
                                                                <div className="absolute top-0 right-0 flex">
                                                                    <div className="bg-gray-700 text-gray-300 text-xs px-2 py-1 rounded-tr">
                                                                        {match[1]}
                                                                    </div>
                                                                    <button
                                                                        className="p-1 bg-gray-700 text-gray-300 hover:text-white rounded-br"
                                                                        onClick={() => copyToClipboard(String(children))}
                                                                        title={t('common.copy')}
                                                                    >
                                                                        <Copy size={14} />
                                                                    </button>
                                                                </div>
                                                                <SyntaxHighlighter
                                                                    style={atomOneDark}
                                                                    language={match[1]}
                                                                    customStyle={{
                                                                        marginTop: '0',
                                                                        paddingTop: '2rem'
                                                                    }}
                                                                    {...props}
                                                                >
                                                                    {String(children).replace(/\n$/, '')}
                                                                </SyntaxHighlighter>
                                                            </div>
                                                        ) : (
                                                            <code className={`${className} bg-gray-700 px-1 py-0.5 rounded text-sm`} {...props}>
                                                                {children}
                                                            </code>
                                                        );
                                                    },
                                                    p: ({ children }) => <p className="mb-4 last:mb-0">{children}</p>,
                                                    ul: ({ children }) => <ul className="list-disc pl-6 mb-4 last:mb-0">{children}</ul>,
                                                    ol: ({ children }) => <ol className="list-decimal pl-6 mb-4 last:mb-0">{children}</ol>,
                                                    li: ({ children }) => <li className="mb-1">{children}</li>,
                                                    h1: ({ children }) => <h1 className="text-xl font-bold mb-4">{children}</h1>,
                                                    h2: ({ children }) => <h2 className="text-lg font-bold mb-3">{children}</h2>,
                                                    h3: ({ children }) => <h3 className="text-md font-bold mb-2">{children}</h3>,
                                                }}
                                            >
                                                {message.content}
                                            </ReactMarkdown>
                                        ) : (
                                            <p className="text-sm">{message.content}</p>
                                        )}
                                        <div
                                            className={`text-xs mt-2 ${
                                                message.type === 'user'
                                                    ? 'text-orange-200'
                                                    : 'text-gray-400'
                                            }`}
                                        >
                                            {new Date(message.timestamp).toLocaleTimeString()}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                        {isLoading && (
                            <div className="flex justify-start">
                                <div className="bg-gray-800 border border-gray-700 rounded-lg p-3">
                                    <div className="flex items-center text-gray-400">
                                        <Loader size={14} className="animate-spin mr-2" />
                                        {t('ai_assistant.typing')}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    <form onSubmit={handleSubmit} className="p-4 border-t border-gray-700 bg-gray-800">
                        <div className="relative">
                            <textarea
                                ref={textareaRef}
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder={t('ai_assistant.input_placeholder')}
                                className="w-full border border-gray-700 rounded-lg pl-3 pr-10 py-2 resize-none bg-gray-700 text-white text-sm"
                                rows={1}
                                disabled={isLoading}
                            />
                            <button 
                                type="submit"
                                disabled={isLoading || !input.trim()}
                                className="absolute right-2 bottom-2 text-orange-500 hover:text-orange-300 disabled:text-gray-600 disabled:cursor-not-allowed"
                            >
                                {isLoading ? 
                                    <Loader size={18} className="animate-spin" /> : 
                                    <Send size={18} />
                                }
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default AIAssistant;